import React, { useMemo } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { ChevronDown, ChevronRight, ImageIcon, Plus, Trash2 } from "lucide-react";
import { Button as UiButton, Select, Switch } from "../../ui";
import { DEFAULT_ASSESSMENT_FORM_UI_CONFIG } from "../../../utils/assessmentFormConfig";
import SubCapabilityQuestionListEditor from "../../SubCapabilityQuestionListEditor";
import { QUESTION_STATE } from "../../../utils/subCapabilityQuestionUtils";
import type { CapabilityFormData } from "./capabilityFormShared";
import type { useCapabilityForm } from "./useCapabilityForm";

type FrameworkOption = { id: number; sourceFrameworkName: string };
type BusinessTypeOption = { id: number; name?: string };

export type CapabilityFormProps = {
  formId: string;
  isEdit: boolean;
  saving: boolean;
  businessTypes: BusinessTypeOption[];
  frameworks: FrameworkOption[];
  onCancel: () => void;
  onDelete?: () => void;
  formData: CapabilityFormData;
} & Omit<
  ReturnType<typeof useCapabilityForm>,
  "setFormData" | "resetExpanded" | "resetForCreate" | "loadFromApi" | "saving"
>;

const countFilledQuestions = (questions: { questionText?: string }[] = []) =>
  questions.filter((q) => String(q?.questionText || "").trim()).length;

export default function CapabilityForm({
  formId,
  isEdit,
  saving,
  businessTypes,
  frameworks,
  onCancel,
  onDelete,
  formData,
  chooseSingleCapabilityAssessment,
  chooseSubCapabilityAssessment,
  expandedSubCaps,
  expandedPanels,
  handleChange,
  setFrameworkId,
  setBusinessTypeId,
  handleSubCapabilityChange,
  handleQuestionListChange,
  handleCapabilityQuestionListChange,
  handleCapabilityAssessmentChange,
  handleCapabilityMaturityOptionChange,
  handleAddCapabilityMaturityOption,
  handleRemoveCapabilityMaturityOption,
  handleMaturityOptionChange,
  handleAddMaturityOption,
  handleRemoveMaturityOption,
  toggleSubCapExpanded,
  togglePanelExpanded,
  addSubCapability,
  removeSubCapability,
  handleSubmit,
}: CapabilityFormProps) {
  const throughSubCapabilities = formData.subCapabilities.length > 0;
  const totalQuestions = useMemo(
    () =>
      countFilledQuestions(formData.currentStateQuestions) +
      countFilledQuestions(formData.futureStateQuestions),
    [formData.currentStateQuestions, formData.futureStateQuestions]
  );
  const defaultMaturity = DEFAULT_ASSESSMENT_FORM_UI_CONFIG.maturityOptions;

  return (
    <Form id={formId} onSubmit={handleSubmit} className="cap-mgmt-form-page__form">
      <div className="cap-mgmt-form cap-mgmt-form-page__body">
        <section className="cap-form-card" aria-labelledby="cap-form-details-title">
          <h2 id="cap-form-details-title" className="cap-form-card__title">Details</h2>
          <div className="cap-form-grid-2">
            <div className="cap-form-field cap-form-field--inline-control">
              <label className="cap-form-label cap-form-label--required" htmlFor="formCapabilityName">
                Name
              </label>
              <input
                id="formCapabilityName"
                className="cap-form-inline-control"
                type="text"
                name="capabilityName"
                placeholder="e.g. Collaborative Leadership"
                value={formData.capabilityName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="cap-form-field cap-form-field--inline-control">
              <label className="cap-form-label" htmlFor="formFrameworkId">Source</label>
              <Select
                id="formFrameworkId"
                name="frameworkId"
                className="cap-form-inline-control-wrap"
                searchable
                placeholder="None"
                aria-label="Source"
                value={formData.frameworkId ? String(formData.frameworkId) : ""}
                onChange={setFrameworkId}
                options={[
                  { value: "", label: "None" },
                  ...frameworks.map((fw) => ({
                    value: String(fw.id),
                    label: fw.sourceFrameworkName,
                  })),
                ]}
              />
            </div>
            <div className="cap-form-field cap-form-field--inline-control">
              <label className="cap-form-label cap-form-label--required" htmlFor="formBusinessTypeId">
                User type
              </label>
              <Select
                id="formBusinessTypeId"
                name="businessTypeId"
                className="cap-form-inline-control-wrap"
                required
                placeholder="Select user type"
                aria-label="User type"
                value={formData.businessTypeId ? String(formData.businessTypeId) : ""}
                onChange={setBusinessTypeId}
                options={[
                  { value: "", label: "Select user type" },
                  ...businessTypes.map((bt) => ({
                    value: String(bt.id),
                    label: bt.name ?? `Type ${bt.id}`,
                  })),
                ]}
              />
            </div>
            <div className="cap-form-field cap-form-field--inline-control cap-form-field--icon-inline">
              <label className="cap-form-label" htmlFor="formCapabilityIconUrl">
                Icon · optional
              </label>
              <div className="cap-form-icon-row">
                <div className="cap-form-icon-preview" aria-hidden>
                  {formData.capabilityIconUrl?.trim() ? (
                    <img src={formData.capabilityIconUrl} alt="" />
                  ) : (
                    <ImageIcon size={18} />
                  )}
                </div>
                <input
                  id="formCapabilityIconUrl"
                  className="cap-form-inline-control"
                  type="text"
                  name="capabilityIconUrl"
                  placeholder="Image URL (e.g. images/Strategic_Management.png)"
                  value={formData.capabilityIconUrl}
                  onChange={handleChange}
                  aria-label="Icon URL"
                />
              </div>
            </div>
            <div className="cap-form-field cap-form-field--span-2">
              <label
                className="cap-form-label cap-form-label--required"
                htmlFor="formCapabilityDescription"
              >
                Short description
              </label>
              <Form.Control
                id="formCapabilityDescription"
                as="textarea"
                rows={3}
                name="capabilityDescription"
                placeholder="Brief description shown when users choose capabilities"
                value={formData.capabilityDescription}
                onChange={handleChange}
                required
              />
              <p className="cap-form-hint">
                {formData.capabilityDescription.length} characters · shown to users when choosing
                capabilities
              </p>
            </div>
          </div>
        </section>

        <section className="cap-form-card" aria-labelledby="cap-form-assess-mode-title">
          <h2 id="cap-form-assess-mode-title" className="cap-form-card__title">
            How is this capability assessed?
          </h2>
          <div className="cap-form-assess-mode">
            <button
              type="button"
              className={`cap-form-assess-option${!throughSubCapabilities ? " is-selected" : ""}`}
              onClick={chooseSingleCapabilityAssessment}
            >
              <span className="cap-form-assess-option__radio" aria-hidden />
              <span>
                <span className="cap-form-assess-option__title">As a single capability</span>
                <span className="cap-form-assess-option__desc">
                  Questions are asked about the capability as a whole.
                </span>
              </span>
            </button>
            <button
              type="button"
              className={`cap-form-assess-option${throughSubCapabilities ? " is-selected" : ""}`}
              onClick={chooseSubCapabilityAssessment}
            >
              <span className="cap-form-assess-option__radio" aria-hidden />
              <span>
                <span className="cap-form-assess-option__title">Through sub-capabilities</span>
                <span className="cap-form-assess-option__desc">
                  Each sub-capability has its own questions. Users can pick some or all.
                </span>
              </span>
            </button>
          </div>
        </section>

        {!throughSubCapabilities ? (
          <>
            <section className="cap-form-card" aria-labelledby="cap-form-questions-title">
              <div className="cap-form-card__title-row">
                <h2 id="cap-form-questions-title" className="cap-form-card__title">
                  Assessment questions
                </h2>
                <span className="cap-form-card__meta">
                  {totalQuestions} {totalQuestions === 1 ? "question" : "questions"}
                </span>
              </div>
              <div className="cap-form-questions-grid">
                <div className="cap-form-questions-col">
                  <h3 className="cap-form-questions-col__title">Current state</h3>
                  <SubCapabilityQuestionListEditor
                    label="Current state"
                    questions={formData.currentStateQuestions || []}
                    stateKind={QUESTION_STATE.current}
                    placeholder="Enter a question"
                    addLabel="Add question"
                    numbered
                    onChange={(next) =>
                      handleCapabilityQuestionListChange(QUESTION_STATE.current, next)
                    }
                  />
                  <div className="cap-form-example-answer">
                    <label className="cap-form-label" htmlFor="current-comment-ph">
                      Example answer · optional
                    </label>
                    <Form.Control
                      id="current-comment-ph"
                      as="textarea"
                      rows={2}
                      placeholder="Shown under the comment box to guide users"
                      value={formData.currentStateCommentPlaceholder || ""}
                      onChange={(e) =>
                        handleCapabilityAssessmentChange(
                          "currentStateCommentPlaceholder",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
                <div className="cap-form-questions-col">
                  <h3 className="cap-form-questions-col__title">Future state</h3>
                  <SubCapabilityQuestionListEditor
                    label="Future state"
                    questions={formData.futureStateQuestions || []}
                    stateKind={QUESTION_STATE.future}
                    placeholder="Enter a question"
                    addLabel="Add question"
                    numbered
                    onChange={(next) =>
                      handleCapabilityQuestionListChange(QUESTION_STATE.future, next)
                    }
                  />
                  <div className="cap-form-example-answer">
                    <label className="cap-form-label" htmlFor="future-comment-ph">
                      Example answer · optional
                    </label>
                    <Form.Control
                      id="future-comment-ph"
                      as="textarea"
                      rows={2}
                      placeholder="Shown under the comment box to guide users"
                      value={formData.futureStateCommentPlaceholder || ""}
                      onChange={(e) =>
                        handleCapabilityAssessmentChange(
                          "futureStateCommentPlaceholder",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="cap-form-card" aria-labelledby="cap-form-maturity-title">
              <div className="cap-form-maturity-head">
                <div>
                  <h2 id="cap-form-maturity-title" className="cap-form-card__title">
                    Maturity scale
                  </h2>
                  <p className="cap-form-maturity-head__subtitle">
                    {formData.useCustomMaturity
                      ? "Custom labels and definitions for this capability"
                      : "Uses the standard 5-level scale"}
                  </p>
                </div>
                <div className="cap-form-maturity-custom">
                  <Switch
                    checked={Boolean(formData.useCustomMaturity)}
                    onCheckedChange={(checked) =>
                      handleCapabilityAssessmentChange("useCustomMaturity", checked)
                    }
                    aria-label="Customise maturity for this capability"
                  />
                  <span>Customise for this capability</span>
                </div>
              </div>

              {!formData.useCustomMaturity ? (
                <div className="cap-form-maturity-pills">
                  {defaultMaturity.map((option) => (
                    <span key={option.key} className="cap-form-maturity-pill">
                      {option.optionValue} · {option.label}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="cap-mgmt-maturity-list">
                  <p className="cap-form-maturity-list__label">Maturity options</p>
                  {(formData.maturityOptions || []).map((option, optionIndex) => (
                    <div
                      key={`${option.optionValue}-${optionIndex}`}
                      className="cap-mgmt-maturity-row dashboard-form-section dashboard-form-section--nested"
                    >
                      <div className="cap-mgmt-maturity-row__fields">
                        <Row className="g-2">
                          <Col md={4} lg={3}>
                            <Form.Label className="cap-mgmt-field-label">Label</Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder="Label"
                              value={option.label || ""}
                              onChange={(e) =>
                                handleCapabilityMaturityOptionChange(optionIndex, {
                                  label: e.target.value,
                                })
                              }
                            />
                          </Col>
                          <Col md={8} lg={9}>
                            <Form.Label className="cap-mgmt-field-label">Definition</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              size="sm"
                              placeholder="Definition"
                              value={option.definition || ""}
                              onChange={(e) =>
                                handleCapabilityMaturityOptionChange(optionIndex, {
                                  definition: e.target.value,
                                })
                              }
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="cap-mgmt-maturity-row__action">
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          className="cap-mgmt-icon-btn"
                          disabled={(formData.maturityOptions || []).length <= 1}
                          onClick={() => handleRemoveCapabilityMaturityOption(optionIndex)}
                          aria-label={`Remove maturity level ${optionIndex + 1}`}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cap-form-add-level"
                    onClick={handleAddCapabilityMaturityOption}
                  >
                    <Plus size={14} aria-hidden />
                    Add level
                  </button>
                </div>
              )}
            </section>
          </>
        ) : null}

        {throughSubCapabilities ? (
          <section className="cap-form-card" aria-labelledby="cap-form-subcaps-title">
            <h2 id="cap-form-subcaps-title" className="cap-form-card__title">Sub-capabilities</h2>
            <p className="cap-form-hint" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
              Each sub-capability has its own questions, example answers, and optional custom
              maturity scale. Expand a row to edit.
            </p>

                {formData.subCapabilities.map((subCap, index) => {
                  const isSubCapExpanded = expandedSubCaps.has(index);
                  const questionsPanelKey = `${index}-questions`;
                  const maturityPanelKey = `${index}-maturity`;
                  const isQuestionsExpanded = expandedPanels.has(questionsPanelKey);
                  const isMaturityExpanded = expandedPanels.has(maturityPanelKey);

                  return (
                  <div
                    key={index}
                    className={`cap-mgmt-subcap-card dashboard-form-section${isSubCapExpanded ? " is-expanded" : " is-collapsed"}`}
                  >
                    <div className="cap-mgmt-subcap-header">
                      <button
                        type="button"
                        className="cap-mgmt-subcap-header__toggle"
                        onClick={() => toggleSubCapExpanded(index)}
                        aria-expanded={isSubCapExpanded}
                        aria-controls={`subcap-body-${index}`}
                      >
                        <span className="cap-mgmt-collapse-chevron" aria-hidden="true">
                          {isSubCapExpanded ? <ChevronDown /> : <ChevronRight />}
                        </span>
                        <span className="cap-mgmt-subcap-badge">{index + 1}</span>
                        <span className="cap-mgmt-subcap-title">
                          {subCap.subCapabilityName?.trim() || `Sub-Capability ${index + 1}`}
                        </span>
                        {!isSubCapExpanded && Boolean(subCap.useCustomMaturity) && (
                          <span className="cap-mgmt-subcap-meta">Custom maturity</span>
                        )}
                      </button>
                      <div className="cap-mgmt-subcap-header__actions">
                        <Form.Check
                          type="switch"
                          id={`subcap-active-${index}`}
                          label="Active"
                          className="cap-mgmt-subcap-active"
                          checked={
                            subCap.isActive !== undefined
                              ? subCap.isActive
                              : true
                          }
                          onChange={(e) =>
                            handleSubCapabilityChange(
                              index,
                              "isActive",
                              e.target.checked
                            )
                          }
                        />
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          className="cap-mgmt-icon-btn"
                          onClick={() => removeSubCapability(index)}
                          aria-label={`Remove sub-capability ${index + 1}`}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>

                    {isSubCapExpanded && (
                    <div id={`subcap-body-${index}`} className="cap-mgmt-subcap-body">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group className="mb-0">
                          <Form.Label>Sub Capability Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter sub capability name"
                            value={subCap.subCapabilityName || ""}
                            onChange={(e) =>
                              handleSubCapabilityChange(
                                index,
                                "subCapabilityName",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-0">
                          <Form.Label>Sub Capability Description</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter sub capability description"
                            value={subCap.subCapabilityDescription || ""}
                            onChange={(e) =>
                              handleSubCapabilityChange(
                                index,
                                "subCapabilityDescription",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className={`cap-mgmt-panel${isQuestionsExpanded ? " is-expanded" : " is-collapsed"}`}>
                      <button
                        type="button"
                        className="cap-mgmt-panel__toggle"
                        onClick={() => togglePanelExpanded(questionsPanelKey)}
                        aria-expanded={isQuestionsExpanded}
                        aria-controls={`subcap-questions-${index}`}
                      >
                        <span className="cap-mgmt-collapse-chevron" aria-hidden="true">
                          {isQuestionsExpanded ? <ChevronDown /> : <ChevronRight />}
                        </span>
                        <h6 className="cap-mgmt-panel__title mb-0">Assessment Questions</h6>
                      </button>

                    {isQuestionsExpanded && (
                    <div id={`subcap-questions-${index}`} className="cap-mgmt-panel__body">
                      <div className="cap-form-questions-grid">
                        <div className="cap-form-questions-col">
                          <h3 className="cap-form-questions-col__title">Current state</h3>
                          <SubCapabilityQuestionListEditor
                            label="Current state"
                            questions={subCap.currentStateQuestions || []}
                            stateKind={QUESTION_STATE.current}
                            placeholder="Enter a question"
                            addLabel="Add question"
                            numbered
                            onChange={(next) =>
                              handleQuestionListChange(index, QUESTION_STATE.current, next)
                            }
                          />
                          <div className="cap-form-example-answer">
                            <label
                              className="cap-form-label"
                              htmlFor={`subcap-${index}-current-comment-ph`}
                            >
                              Example answer · optional
                            </label>
                            <Form.Control
                              id={`subcap-${index}-current-comment-ph`}
                              as="textarea"
                              rows={2}
                              placeholder="Shown under the comment box to guide users"
                              value={subCap.currentStateCommentPlaceholder || ""}
                              onChange={(e) =>
                                handleSubCapabilityChange(
                                  index,
                                  "currentStateCommentPlaceholder",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="cap-form-questions-col">
                          <h3 className="cap-form-questions-col__title">Future state</h3>
                          <SubCapabilityQuestionListEditor
                            label="Future state"
                            questions={subCap.futureStateQuestions || []}
                            stateKind={QUESTION_STATE.future}
                            placeholder="Enter a question"
                            addLabel="Add question"
                            numbered
                            onChange={(next) =>
                              handleQuestionListChange(index, QUESTION_STATE.future, next)
                            }
                          />
                          <div className="cap-form-example-answer">
                            <label
                              className="cap-form-label"
                              htmlFor={`subcap-${index}-future-comment-ph`}
                            >
                              Example answer · optional
                            </label>
                            <Form.Control
                              id={`subcap-${index}-future-comment-ph`}
                              as="textarea"
                              rows={2}
                              placeholder="Shown under the comment box to guide users"
                              value={subCap.futureStateCommentPlaceholder || ""}
                              onChange={(e) =>
                                handleSubCapabilityChange(
                                  index,
                                  "futureStateCommentPlaceholder",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    )}
                    </div>

                    <div className={`cap-mgmt-panel cap-mgmt-panel--maturity dashboard-form-section dashboard-form-section--nested${isMaturityExpanded ? " is-expanded" : " is-collapsed"}`}>
                      <button
                        type="button"
                        className="cap-mgmt-panel__toggle"
                        onClick={() => togglePanelExpanded(maturityPanelKey)}
                        aria-expanded={isMaturityExpanded}
                        aria-controls={`subcap-maturity-${index}`}
                      >
                        <span className="cap-mgmt-collapse-chevron" aria-hidden="true">
                          {isMaturityExpanded ? <ChevronDown /> : <ChevronRight />}
                        </span>
                        <h6 className="cap-mgmt-panel__title mb-0">Maturity Scale</h6>
                        {Boolean(subCap.useCustomMaturity) && (
                          <span className="cap-mgmt-subcap-meta">Custom</span>
                        )}
                      </button>

                      {isMaturityExpanded && (
                      <div id={`subcap-maturity-${index}`} className="cap-mgmt-panel__body cap-mgmt-panel__body--maturity">
                        <div className="cap-form-maturity-head">
                          <div>
                            <p className="cap-form-maturity-head__title">Maturity scale</p>
                            <p className="cap-form-maturity-head__subtitle">
                              {subCap.useCustomMaturity
                                ? "Custom labels and definitions for this sub-capability"
                                : "Uses the standard 5-level scale"}
                            </p>
                          </div>
                          <div className="cap-form-maturity-custom">
                            <Switch
                              checked={Boolean(subCap.useCustomMaturity)}
                              onCheckedChange={(checked) =>
                                handleSubCapabilityChange(index, "useCustomMaturity", checked)
                              }
                              aria-label="Customise maturity for this sub-capability"
                            />
                            <span>Customise for this sub-capability</span>
                          </div>
                        </div>

                        {!subCap.useCustomMaturity ? (
                          <div className="cap-form-maturity-pills">
                            {defaultMaturity.map((option) => (
                              <span key={option.key} className="cap-form-maturity-pill">
                                {option.optionValue} · {option.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="cap-mgmt-maturity-list">
                            <p className="cap-form-maturity-list__label">Maturity options</p>
                            {(subCap.maturityOptions || []).map((option, optionIndex) => (
                              <div
                                key={`${option.optionValue}-${optionIndex}`}
                                className="cap-mgmt-maturity-row dashboard-form-section dashboard-form-section--nested"
                              >
                                <div className="cap-mgmt-maturity-row__fields">
                                  <Row className="g-2">
                                    <Col md={4} lg={3}>
                                      <Form.Label className="cap-mgmt-field-label">Label</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        placeholder="Label"
                                        value={option.label || ""}
                                        onChange={(e) =>
                                          handleMaturityOptionChange(index, optionIndex, {
                                            label: e.target.value,
                                          })
                                        }
                                      />
                                    </Col>
                                    <Col md={8} lg={9}>
                                      <Form.Label className="cap-mgmt-field-label">
                                        Definition
                                      </Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        placeholder="Definition"
                                        value={option.definition || ""}
                                        onChange={(e) =>
                                          handleMaturityOptionChange(index, optionIndex, {
                                            definition: e.target.value,
                                          })
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                                <div className="cap-mgmt-maturity-row__action">
                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    className="cap-mgmt-icon-btn"
                                    disabled={(subCap.maturityOptions || []).length <= 1}
                                    onClick={() =>
                                      handleRemoveMaturityOption(index, optionIndex)
                                    }
                                    aria-label={`Remove maturity level ${optionIndex + 1}`}
                                  >
                                    <Trash2 />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="cap-form-add-level"
                              onClick={() => handleAddMaturityOption(index)}
                            >
                              <Plus size={14} aria-hidden />
                              Add level
                            </button>
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                    </div>
                    )}
                  </div>
                  );
                })}

                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={addSubCapability}
                  className="cap-mgmt-add-btn cap-form-add-sub"
                >
                  <Plus className="me-2" />
                  Add sub-capability
                </Button>
          </section>
        ) : null}
      </div>

      <footer className="cap-form-page-footer" aria-label="Capability form actions">
        <div className="cap-form-page-footer__inner">
          {isEdit && onDelete ? (
            <button type="button" className="cap-form-delete-btn" onClick={onDelete}>
              Delete capability
            </button>
          ) : (
            <span className="cap-form-page-footer__spacer" aria-hidden />
          )}
          <div className="cap-form-page-footer__actions">
            <UiButton type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </UiButton>
            <UiButton type="submit" variant="primary" size="sm" disabled={saving}>
              {saving ? "Saving…" : "Save capability"}
            </UiButton>
          </div>
        </div>
      </footer>
    </Form>
  );
}
